import { captureCustomizerMessage } from "./sentry";

const memoryStorage = new Map();
const reportedStorageIssues = new Set();

function reportStorageIssue(message, details = {}) {
    const issueKey = `${message}:${details.operation || ""}:${details.key || ""}`;
    if (reportedStorageIssues.has(issueKey)) return;

    reportedStorageIssues.add(issueKey);
    captureCustomizerMessage(message, {
        level: "error",
        tags: {
            key: details.key || "unknown",
            operation: details.operation || "unknown",
        },
        context: {
            storageMode: "memory_fallback",
            errorName: details.error?.name,
            errorMessage: details.error?.message,
        },
    });
}

function getStorage() {
    try {
        if (typeof window === "undefined" || !window.localStorage) {
            return null;
        }

        const testKey = "__customizer_storage_test__";
        window.localStorage.setItem(testKey, "1");
        window.localStorage.removeItem(testKey);
        return window.localStorage;
    } catch (error) {
        reportStorageIssue("local_storage_unavailable", {
            operation: "access",
            error,
        });
        return null;
    }
}

function getMemoryItem(key) {
    return memoryStorage.has(key) ? memoryStorage.get(key) : null;
}

export function safeGetItem(key, defaultValue = null) {
    if (!key) return defaultValue;

    const storage = getStorage();
    if (!storage) {
        return getMemoryItem(key) ?? defaultValue;
    }

    try {
        const value = storage.getItem(key);
        return value === null ? defaultValue : value;
    } catch (error) {
        reportStorageIssue("local_storage_unavailable", {
            key,
            operation: "getItem",
            error,
        });
        return getMemoryItem(key) ?? defaultValue;
    }
}

export function safeSetItem(key, value) {
    if (!key) return false;

    memoryStorage.set(key, String(value));

    const storage = getStorage();
    if (!storage) return false;

    try {
        storage.setItem(key, String(value));
        return true;
    } catch (error) {
        reportStorageIssue("local_storage_unavailable", {
            key,
            operation: "setItem",
            error,
        });
        return false;
    }
}

export function safeRemoveItem(key) {
    if (!key) return false;

    memoryStorage.delete(key);

    const storage = getStorage();
    if (!storage) return false;

    try {
        storage.removeItem(key);
        return true;
    } catch (error) {
        reportStorageIssue("local_storage_unavailable", {
            key,
            operation: "removeItem",
            error,
        });
        return false;
    }
}

export function safeGetJson(key, defaultValue = {}) {
    const rawValue = safeGetItem(key, null);
    if (!rawValue) return defaultValue;

    try {
        return JSON.parse(rawValue) || defaultValue;
    } catch (error) {
        reportStorageIssue("local_storage_invalid_json", {
            key,
            operation: "JSON.parse",
            error,
        });
        safeRemoveItem(key);
        return defaultValue;
    }
}

export function safeSetJson(key, value) {
    try {
        return safeSetItem(key, JSON.stringify(value));
    } catch (error) {
        reportStorageIssue("local_storage_unavailable", {
            key,
            operation: "JSON.stringify",
            error,
        });
        return false;
    }
}

